import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Clock, ArrowLeft, FileText, Users, MapPin, Coins, TrendingUp } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { EvidenceBadges } from "../components/EvidenceBadges";
import { StageBadge, StageAction } from "../components/ProjectStage";

const NGN = (n) => `₦${Number(n || 0).toLocaleString()}`;
const NICE = (k) => (k || "").replace(/_/g, " ");

export default function ProjectTrace() {
  const { serial } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    document.title = `Project trace ${serial} | Dutsen Kauna Foundation`;
    api.get(`/projects/trace/${encodeURIComponent(serial)}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [serial]);

  if (err) return <NotFound serial={serial} message={err} />;
  if (!data) return <div className="py-24 text-center text-[#0a0d12]/60">Loading trace…</div>;

  const { application, project, donations, donation_total_ngn, donation_count } = data;
  const raised = project?.raised_amount || donation_total_ngn || 0;
  const target = project?.target_amount_numeric || 0;
  const pct = target > 0 ? Math.min(100, Math.round((raised / target) * 100)) : 0;

  return (
    <div data-testid="project-trace-page" className="bg-[#f8f4e8]">
      {/* Hero */}
      <section className="relative bg-[#155e3f] text-white py-16 md:py-20 overflow-hidden">
        {project?.image_url && <img src={project.image_url} alt="Project site photograph" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-20" />}
        <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] to-[#155e3f]/50" />
        <div className="dk-container relative">
          <Link to="/projects" className="text-[#d6aa43] text-sm font-semibold flex items-center gap-1 mb-4 w-fit"><ArrowLeft size={14} /> All projects</Link>
          <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">Complete lifecycle trace</span>
          <h1 className="font-serif text-3xl md:text-5xl font-bold mt-3 max-w-3xl">{project?.title || "Project trace"}</h1>
          {project?.serial && <div className="font-mono text-[#d6aa43] mt-2 text-sm flex items-center gap-3">{project.serial} <StageBadge project={project} /></div>}
          {project && <div className="mt-5 p-4 rounded-xl bg-white/5 border border-white/10 max-w-3xl" data-testid="trace-evidence"><div className="text-[10px] uppercase tracking-[0.2em] text-[#d6aa43] font-bold mb-2">What has been verified — click any badge</div><EvidenceBadges project={project} size="md" /></div>}
          <div className="mt-6 flex flex-wrap gap-3">
            {project && <StageAction project={project} />}
          </div>
        </div>
      </section>

      {/* Trace timeline */}
      <section className="py-16">
        <div className="dk-container max-w-4xl">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Application */}
            <div className="bg-white rounded-2xl p-6 border border-black/5" data-testid="trace-application">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="text-[#d6aa43]" size={18} />
                <div className="text-xs uppercase tracking-[0.22em] text-[#0d1724]/60 font-bold">Originating application</div>
              </div>
              {application ? (
                <>
                  <div className="font-mono text-sm text-[#0a0d12] mb-1">{application.serial}</div>
                  <div className="text-sm text-[#0a0d12]/70 mb-2">Category: <span className="capitalize font-semibold">{NICE(application.primary_category)}</span></div>
                  <div className="text-sm text-[#0a0d12]/70 mb-2">Applicant: <span className="font-semibold">{application.full_name || application.organisation_name || application.community_name || "—"}</span></div>
                  <div className="text-sm text-[#0a0d12]/70 mb-2">Status: <span className="capitalize font-semibold text-[#0d1724]">{NICE(application.status)}</span></div>
                  {application.problem_description && (<p className="text-sm text-[#0a0d12]/80 mt-2 italic">"{application.problem_description.slice(0, 220)}{application.problem_description.length > 220 ? "…" : ""}"</p>)}
                </>
              ) : (
                <div className="text-sm text-[#0a0d12]/60">No originating application recorded for this project.</div>
              )}
            </div>

            {/* Project */}
            <div className="bg-[#155e3f] text-white rounded-2xl p-6" data-testid="trace-project">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="text-[#d6aa43]" size={18} />
                <div className="text-xs uppercase tracking-[0.22em] text-[#d6aa43] font-bold">Approved project</div>
              </div>
              {project ? (
                <>
                  <div className="font-mono text-sm text-[#d6aa43] mb-1">{project.serial}</div>
                  <div className="text-sm mb-1"><MapPin size={12} className="inline text-[#d6aa43]" /> {project.location || "—"}</div>
                  {project.beneficiaries_estimate > 0 && <div className="text-sm mb-1"><Users size={12} className="inline text-[#d6aa43]" /> {project.beneficiaries_estimate} beneficiaries</div>}
                  <div className="text-sm text-white mb-3">Status: <span className="font-semibold capitalize text-white">{NICE(project.status)}</span></div>
                  <p className="text-sm text-white">{project.short_description}</p>
                </>
              ) : (
                <div className="text-sm text-white">This application has not yet been converted into a public project.</div>
              )}
            </div>
          </div>

          {/* Funding */}
          {project && (
            <div className="mt-6 bg-white rounded-2xl p-6 border border-black/5" data-testid="trace-funding">
              <div className="flex items-center gap-2 mb-3">
                <Coins className="text-[#d6aa43]" size={18} />
                <div className="text-xs uppercase tracking-[0.22em] text-[#0d1724]/60 font-bold">Funding progress</div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3 mb-3">
                <div><div className="text-[10px] uppercase text-[#0a0d12]/60">Target</div><div className="font-serif text-xl font-bold">{target ? NGN(target) : project.target_amount || "TBC"}</div></div>
                <div><div className="text-[10px] uppercase text-[#0a0d12]/60">Raised</div><div className="font-serif text-xl font-bold text-[#d6aa43]">{NGN(raised)}</div></div>
                <div><div className="text-[10px] uppercase text-[#0a0d12]/60">Donations</div><div className="font-serif text-xl font-bold">{donation_count || 0}</div></div>
              </div>
              <div className="h-2 rounded-full bg-[#f8f4e8] overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#d6aa43] to-[#f0ce77]" style={{ width: `${pct}%` }} />
              </div>
              <div className="text-xs text-[#0a0d12]/60 mt-1">{pct}% funded</div>
            </div>
          )}

          {/* Donations list */}
          {donations.length > 0 && (
            <div className="mt-6 bg-white rounded-2xl p-6 border border-black/5" data-testid="trace-donations">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="text-[#d6aa43]" size={18} />
                <div className="text-xs uppercase tracking-[0.22em] text-[#0d1724]/60 font-bold">Contributions</div>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {donations.map((d, i) => (
                  <div key={d.serial || d.id || `${d.donor_name}-${d.date}-${i}`} className="flex items-center justify-between p-3 rounded-md bg-[#f8f4e8] border border-black/5 text-sm">
                    <div>
                      <div className="font-semibold text-[#0a0d12]">{d.donor_name}</div>
                      <div className="text-[10px] font-mono text-[#0a0d12]/60">{d.serial} · {new Date(d.date).toLocaleDateString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-serif font-bold text-[#0d1724]">{d.currency} {Number(d.amount).toLocaleString()}</div>
                      <div className="text-[10px] uppercase text-[#0a0d12]/60">{d.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function NotFound({ serial, message }) {
  return (
    <div className="py-24 text-center">
      <div className="dk-container max-w-lg">
        <Clock className="text-[#d6aa43] mx-auto mb-3" size={36} />
        <h1 className="font-serif text-3xl font-bold text-[#0a0d12]">No trace found</h1>
        <div className="mt-2 font-mono text-sm text-[#0a0d12]/60">{serial}</div>
        <p className="mt-4 text-[#0a0d12]/70">{message || "This reference could not be traced."}</p>
        <Link to="/projects" className="dk-btn-primary mt-6">Back to projects</Link>
      </div>
    </div>
  );
}
